import { createSlice } from '@reduxjs/toolkit';
import { RESET_ALL_REDUCERS } from 'actions';
import {
    CAPTURE_FLOW_MODES,
    DOCUMENT_STATUS,
    SELFIE_STATUS,
} from 'features/govtId/v2/helper/constants';

const DOCUMENT_INITIAL = {
    transactionId: null,
    images: [],
    status: DOCUMENT_STATUS.IDLE,
    errorCode: null,
};

const SELFIE_INITIAL = {
    // AWS Rekognition session id, minted by our backend.
    sessionId: null,
    status: SELFIE_STATUS.IDLE,
    failureReason: null,
};

const CAMERA_PERMISSION_GUIDE_INITIAL = {
    showGuide: false,
    properties: {},
};

// Failed checks in a row with one capture mode, for the sheet that suggests the other mode.
const MODE_GUIDANCE_INITIAL = {
    streak: 0,
    mode: null,
    shown: false,
};

const UI_INITIAL = {
    captureFlowMode: CAPTURE_FLOW_MODES.AUTO,
    modeGuidance: { ...MODE_GUIDANCE_INITIAL },
    // Tips carousel, shown over the instructions and failure screens.
    showInstructions: false,
    instructionType: null,
    // Browser-permission guide, a shared modal each flow keeps its own copy of.
    cameraPermissionGuide: { ...CAMERA_PERMISSION_GUIDE_INITIAL },
};

const initialState = {
    document: { ...DOCUMENT_INITIAL },
    selfie: { ...SELFIE_INITIAL },
    ui: { ...UI_INITIAL },
};

const govtIdV2Slice = createSlice({
    name: 'govtIdV2',
    initialState,
    reducers: {
        setTransactionId: (state, action) => {
            state.document.transactionId = action.payload ?? null;
        },

        setImages: (state, action) => {
            state.document.images = action.payload ?? [];
        },

        /**
         * Adds what a capture returned to what earlier ones did, for a document collected a page at a
         * time. Duplicates are dropped by image data, because a page's result is not guaranteed to
         * describe only that page, and the ids are re-derived so the previews stay keyed in order.
         */
        appendImages: (state, action) => {
            const existing = new Set(state.document.images.map(({ src }) => src));
            const added = (action.payload ?? []).filter(({ src }) => src && !existing.has(src));

            state.document.images = [...state.document.images, ...added].map((image, id) => ({
                ...image,
                id,
            }));
        },

        setDocumentStatus: (state, action) => {
            state.document.status = action.payload ?? DOCUMENT_STATUS.IDLE;
        },

        setDocumentErrorCode: (state, action) => {
            state.document.errorCode = action.payload ?? null;
        },

        resetDocument: (state) => {
            state.document = { ...DOCUMENT_INITIAL };
        },

        setSelfieSessionId: (state, action) => {
            state.selfie.sessionId = action.payload ?? null;
        },

        setSelfieStatus: (state, action) => {
            state.selfie.status = action.payload ?? SELFIE_STATUS.IDLE;
        },

        setSelfieFailureReason: (state, action) => {
            state.selfie.failureReason = action.payload ?? null;
        },

        resetSelfie: (state) => {
            state.selfie = { ...SELFIE_INITIAL };
        },

        setCaptureFlowMode: (state, action) => {
            state.ui.captureFlowMode = action.payload ?? CAPTURE_FLOW_MODES.AUTO;
        },

        // A failure in another mode starts a new run.
        recordModeFailure: (state, action) => {
            const guidance = state.ui.modeGuidance;

            guidance.streak = guidance.mode === action.payload ? guidance.streak + 1 : 1;
            guidance.mode = action.payload;
        },

        resetModeFailures: (state) => {
            state.ui.modeGuidance.streak = 0;
        },

        markModeGuidanceShown: (state) => {
            state.ui.modeGuidance.shown = true;
        },

        openInstructions: (state, action) => {
            state.ui.showInstructions = true;
            state.ui.instructionType = action.payload ?? null;
        },

        closeInstructions: (state) => {
            state.ui.showInstructions = false;
            state.ui.instructionType = null;
        },

        showCameraPermissionGuide: (state, action) => {
            state.ui.cameraPermissionGuide = { showGuide: true, properties: action.payload ?? {} };
        },

        clearCameraPermissionGuide: (state) => {
            state.ui.cameraPermissionGuide = { ...CAMERA_PERMISSION_GUIDE_INITIAL };
        },

        resetFlow: (state) => {
            state.document = { ...DOCUMENT_INITIAL };
            state.selfie = { ...SELFIE_INITIAL };
        },
    },
    extraReducers: (builder) => {
        // App-wide reset on session teardown (new token / re-auth).
        builder.addCase(RESET_ALL_REDUCERS, () => initialState);
    },
});

export const {
    setTransactionId,
    setImages,
    appendImages,
    setDocumentStatus,
    setDocumentErrorCode,
    resetDocument,
    setSelfieSessionId,
    setSelfieStatus,
    setSelfieFailureReason,
    resetSelfie,
    setCaptureFlowMode,
    recordModeFailure,
    resetModeFailures,
    markModeGuidanceShown,
    openInstructions,
    closeInstructions,
    showCameraPermissionGuide,
    clearCameraPermissionGuide,
    resetFlow,
} = govtIdV2Slice.actions;

export default govtIdV2Slice.reducer;
